import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  Alert,
  AlertColor,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import StickyFormActions from '~/components/Common/Forms/StickyFormActions';
import SummaryToggleAccordion from '~/components/Common/Forms/SummaryToggleAccordion';
import { promoIdPath } from '~/components/Promo/promoUrls';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import { usePromo } from '~/hooks/usePromo';

import {
  CreateOrUpdatePromoPayload,
  PromoBrands,
  PromoCondition,
  PromoLoyaltyMetaConfig,
  PromoMeta,
  PromoToggles,
  createOrUpdatePromoCondition,
  hasAdvanceRestrictions,
  prepareLoyaltyPointsBonusConfig,
  promoTypes,
} from '~/services/PromoService';
import { addDays, addYears, formatDateLongISO } from '~/services/TimeService';

import { PROMO_DEVICE_TYPES } from '../../consts/promo';
import DateTimeWidget from '../Common/Elements/DateTimeWidget';
import Spinner from '../Common/Spinner';
import PromoConditions from '../Promo/PromoConditions';
import { PromoReplacementExplainer } from '../Promo/PromoReplacementExplainer';
import PromoUserBatchUpload from '../Promo/PromoUser/PromoUserBatchUpload';
import PromoUsers from '../Promo/PromoUsers';
import { PendingCopiedPromoCondition, pendingPromoConditionToCreatePayload } from '../Promo/promoConditionCopyUtils';

import AllowedPaymentBrandSelect from './AllowedPaymentBrandSelect';
import AllowedPaymentMethodSelect from './AllowedPaymentMethodSelect';
import LoyaltyBonusApplyMethodSelector from './LoyaltyBonusApplyMethodSelector';
import ParentPromoUsageLimitWarning from './ParentPromoUsageLimitWarning';
import PromoApproveButton from './PromoApproveButton';
import PromoChildSearchResults from './PromoChildSearchResults';
import PromoDiscountFormV4 from './PromoDiscountFormV4';
import PromoFormSection from './PromoFormSection';
import PromoSummaryActions from './PromoSummaryActions';
import { CategoryTagSelector, DepartmentTagSelector, SubCategoryTagSelector } from './PromoTagSelectors';
import PromoWarningAction from './PromoWarningActions';
import { hasIsParentPromoToggle } from './cloneParentPromoRequestToggle';
import { normalizeLoyaltyBonusApplyMethod } from './normalizeLoyaltyBonusApplyMethod';
import { addCopiedDiscountIdToConditionFilter } from './promoDiscountConditionUtils';

interface PromoFormType {
  promoCodeId?: string;
  code_name: string;
  description: string;
  department_tag: string;
  promo_type: PromoMeta['promo_types'][number]['key'];
  expires_at: Date;
  code_limit: number;
  first_time_purchase_only: boolean;
  first_time_app_purchase_only: boolean;
  limit_per_user: number;
  limit_per_user_per_year?: number | null;
  device_types: Array<string>;
  allowed_payment_methods: Array<string>;
  allowed_airline_carriers: Array<string>;
  allowed_payment_brands: Array<string>;
  is_corporate?: string;
  corporate_name?: string;
  email_domains?: string;
  email_addresses?: string;
  bin_numbers?: string;
  offer_whitelist?: string;
  offer_blacklist?: string;
  dept_tag: string;
  category_tag: string;
  sub_category_tag: string;
  promo_toggles: Array<PromoToggles[number]['key']>;
  popup_message: string;
  usage_count?: number;
  loyalty_bonus_apply_method: PromoLoyaltyMetaConfig['loyaltyBonusFilterMethods'][number]['key'];
}

// Having a presenter to ensure we only send relevant data when update
const promoPresenter = (data: PromoFormType, brand: string): CreateOrUpdatePromoPayload['promo'] => {
  return {
    code_name: data.code_name,
    description: data.description,
    promo_type: data.promo_type,
    expires_at: typeof data.expires_at === 'string' ? data.expires_at : data.expires_at.toISOString(),
    code_limit: data.code_limit,
    limit_per_user: data.limit_per_user,
    // Omit an unset yearly limit so changing another field cannot turn it into 0.
    limit_per_user_per_year: data.limit_per_user_per_year ?? undefined,
    first_time_purchase_only: data.first_time_purchase_only,
    first_time_app_purchase_only: data.first_time_app_purchase_only,
    device_types: data.device_types as unknown as CreateOrUpdatePromoPayload['promo']['device_types'],
    allowed_payment_methods:
      data.allowed_payment_methods as unknown as CreateOrUpdatePromoPayload['promo']['allowed_payment_methods'],
    allowed_payment_brands:
      data.allowed_payment_brands as unknown as CreateOrUpdatePromoPayload['promo']['allowed_payment_brands'],
    is_corporate: !!data.is_corporate,
    corporate_name: data.is_corporate ? data.corporate_name : undefined,
    email_domains:
      data.email_domains
        ?.split(',')
        .map((email) => email.trim())
        .filter((email) => email.length > 0) ?? [],
    brand: brand as PromoBrands,
    email_addresses: data?.email_addresses
      ? data?.email_addresses
          ?.split(',')
          ?.map((email) => email?.trim())
          .filter((email) => email.length > 0)
      : [],
    offer_whitelist: data?.offer_whitelist
      ? data?.offer_whitelist
          ?.split(',')
          ?.map((offer) => offer?.trim())
          .filter((offer) => offer.length > 0)
      : [],
    offer_blacklist: data?.offer_blacklist
      ? data?.offer_blacklist
          ?.split(',')
          ?.map((offer) => offer?.trim())
          .filter((offer) => offer.length > 0)
      : [],
    bin_numbers: data?.bin_numbers
      ? data?.bin_numbers
          ?.split(',')
          ?.map((bin) => bin?.trim())
          .filter((bin) => bin.length > 0)
      : [],
    dept_tag: data.dept_tag,
    category_tag: data.category_tag,
    sub_category_tag: data.sub_category_tag,
    promo_toggles: data.promo_toggles ?? [],
    popup_message: data.popup_message,
    allowed_airline_carriers: data?.allowed_airline_carriers ?? [],
    loyalty_bonus_apply_method: normalizeLoyaltyBonusApplyMethod(data.loyalty_bonus_apply_method),
  };
};

interface Props {
  promoCodeId?: string;
  promoCodeName?: string;
  rerender?: () => void;
  isDev: boolean;
}

const formDefaultValues = {
  code_name: '',
  description: '',
  department_tag: '',
  promo_type: 'percentage',
  expires_at: formatDateLongISO(addYears(1)),
  code_limit: 1,
  limit_per_user: 1,
  limit_per_user_per_year: 0,
  first_time_purchase_only: false,
  first_time_app_purchase_only: false,
  device_types: [],
  allowed_payment_methods: [],
  allowed_payment_brands: [],
  allowed_airline_carriers: [],
  is_corporate: false,
  usage_count: 0,
  corporate_name: '',
  email_domains: '',
  email_addresses: '',
  offer_whitelist: '',
  offer_blacklist: '',
  bin_numbers: '',
  dept_tag: '',
  category_tag: '',
  sub_category_tag: '',
  promo_toggles: [],
  popup_message: '',
  loyalty_bonus_apply_method: 'none',
};

function PromoFormV4({ promoCodeId, promoCodeName, rerender, isDev }: Props) {
  const {
    promo,
    promoParent,
    promoMeta,
    promoLoyaltyMetaConfig,
    promoCodeConditionTypeConfig,
    updatePromo,
    createPromo,
    initPromoData,
    checkPromo,
    promoCheckStatus,
    errors,
    setPromoTagSelection,
    promoTagArrays,
    promoTagSelections,
    createSinglePromoClone,
    isLoading,
    isPromoMetaLoading,
    isPromoBetaUser,
    savePromoCreateTemplate,
  } = usePromo({
    options: {
      autoSelectTag: true,
    },
    onPromoUpdate: (promo) => {
      if (promo) {
        setPromoDiscountData(promo.promo_discounts);
      }
    },
    onTagSelectionChange: (field, value) => {
      setValue(field, value ?? '');
    },
  });

  const { control, handleSubmit, watch, setValue, reset, formState } = useForm({
    mode: 'onChange',
    defaultValues: formDefaultValues,
  });
  const { isDirty, isSubmitting } = formState;
  /** When true, allow the next in-app navigation without prompting (e.g. post-create redirect). */
  const bypassUnsavedNavigationBlockRef = useRef(false);
  /** Create-mode discounts are buffered locally until the promo itself is submitted. */
  const createPromoDiscountBaselineRef = useRef('[]');
  const [promoDiscountData, setPromoDiscountData] = useState<Array<App.PromoDiscount>>(promo?.promo_discounts ?? []);
  const [pendingCopiedPromoConditions, setPendingCopiedPromoConditions] = useState<Array<PendingCopiedPromoCondition>>(
    [],
  );
  const [selectedDiscountIds, setSelectedDiscountIds] = useState<Array<string> | 'all'>([]);
  const [selectedConditionIds, setSelectedConditionIds] = useState<Array<string>>([]);
  const promoType = watch('promo_type');

  const isCorporateBenefitOn = watch('is_corporate');

  const offerWhiteList = watch('offer_whitelist');
  const offerBlackList = watch('offer_blacklist');
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const [showAdvanced, setShowAdvanced] = useState(hasAdvanceRestrictions(promo));
  const { enqueueSnackbar } = useSnackbar();
  const { block, replace } = useAppNavigation();
  const { search } = useLocation();
  const { tenant } = useCurrentTenant();
  const promoTypeOptions = promoTypes(promoMeta?.promo_types);
  const promoDiscountDataSignature = JSON.stringify(promoDiscountData ?? []);
  const hasUnsavedPromoDiscountChanges =
    !promo?.id_promo_code && promoDiscountDataSignature !== createPromoDiscountBaselineRef.current;
  const hasUnsavedConditionChanges = pendingCopiedPromoConditions.length > 0;
  const hasUnsavedChanges = isDirty || hasUnsavedPromoDiscountChanges || hasUnsavedConditionChanges;

  // Custom rerender function that also refreshes promo data in this component
  const handleRerender = useCallback(async () => {
    // Call the parent rerender function
    if (rerender) {
      rerender();
    }

    // Also refresh the promo data in this component
    if (promoCodeId) {
      await initPromoData(
        {
          promoId: promoCodeId,
        },
        true,
      );
    } else if (promoCodeName) {
      await initPromoData(
        {
          codeName: promoCodeName,
          brand: tenant.value,
        },
        true,
      );
    }
  }, [rerender, initPromoData, promoCodeId, promoCodeName, tenant.value]);

  const [hasSubmitted, setHasSubmitted] = useState(false);

  const onSubmit = async (data) => {
    setHasSubmitted(true);
    try {
      const filteredData = promoPresenter(data, brand);
      const persistPendingCopiedConditions = async (promoCodeId: string) => {
        if (pendingCopiedPromoConditions.length === 0) {
          return;
        }
        await Promise.all(
          pendingCopiedPromoConditions.map((condition) =>
            createOrUpdatePromoCondition({
              promoCodeCondition: pendingPromoConditionToCreatePayload(condition, promoCodeId),
            }),
          ),
        );
        setPendingCopiedPromoConditions([]);
      };

      if (promo?.id_promo_code) {
        await updatePromo({
          promo: filteredData,
        });
        await persistPendingCopiedConditions(promo.id_promo_code);
        reset(data);
        await handleRerender();
      } else {
        const newPromoCode = await createPromo({
          promo: filteredData,
          promoDiscounts: promoDiscountData.map((pd) => ({
            ...pd,
            loyalty_points_bonus_config: prepareLoyaltyPointsBonusConfig(pd.loyalty_points_bonus_config),
          })),
        });
        savePromoCreateTemplate({
          dept_tag: filteredData.dept_tag || null,
          category_tag: filteredData.category_tag || null,
          sub_category_tag: filteredData.sub_category_tag || null,
        });
        // Create promo discounts following the promo code
        await Promise.all(
          promoDiscountData.map(async (discount) => {
            const payload = {
              id_promo_code: newPromoCode.result.id_promo_code,
              id_promo_code_discount: discount.id_promo_code_discount,
              discount_value: discount.discount_value,
              max_discount: discount.max_discount,
              subscription_item_discount_value: discount.subscription_item_discount_value,
              subscription_item_discount_type: discount.subscription_item_discount_type,
              min_spend: discount.min_spend,
              products: discount.products,
              region: discount.region,
              loyalty_points_bonus_config: prepareLoyaltyPointsBonusConfig(discount.loyalty_points_bonus_config),
            };
            return updatePromo({
              promo: {
                code_name: newPromoCode.result.code_name,
                brand: newPromoCode.result.brand,
              },
              promoDiscount: payload,
            });
          }),
        );
        await persistPendingCopiedConditions(newPromoCode.result.id_promo_code);
        reset(data);
        createPromoDiscountBaselineRef.current = promoDiscountDataSignature;
        setTimeout(() => {
          bypassUnsavedNavigationBlockRef.current = true;
          replace(`${promoIdPath(newPromoCode.result.id_promo_code)}${search}`);
        }, 2000);
        enqueueSnackbar(
          `Success: ${promo !== null ? 'Your changes have been saved.' : 'Your promo code has been created.'}`,
          {
            autoHideDuration: 5000,
          },
        );
        return;
      }
      enqueueSnackbar(
        `Success: ${promo !== null ? 'Your changes have been saved.' : 'Your promo code has been created.'}`,
        {
          autoHideDuration: 5000,
        },
      );
    } catch (err) {
      if ('errors' in err && err.errors?.length > 0) {
        console.error(`Error: ${JSON.stringify(err, null, 4)}`);
        enqueueSnackbar(`Error: ${JSON.stringify(err, null, 4)}`, {
          variant: 'error',
          autoHideDuration: 5000,
        });
      } else {
        console.error(`Error: ${err.message}`);
        enqueueSnackbar(`${err.message}`, {
          autoHideDuration: 5000,
        });
      }
    }
  };

  useEffect(() => {
    if (!isCorporateBenefitOn) {
      setValue('corporate_name', '');
      setValue('email_domains', '');
    }
  }, [isCorporateBenefitOn, setValue]);

  useEffect(() => {
    if (promo?.id_promo_code) {
      return;
    }
    createPromoDiscountBaselineRef.current = '[]';
  }, [promo?.id_promo_code]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }
    const unblock = block(() => {
      if (bypassUnsavedNavigationBlockRef.current) {
        bypassUnsavedNavigationBlockRef.current = false;
        return undefined;
      }
      const shouldLeave = window.confirm('You have unsaved changes. Are you sure you want to leave this page?');
      if (!shouldLeave) {
        return false;
      }
      return undefined;
    });
    return () => unblock();
  }, [hasUnsavedChanges, block]);

  useEffect(() => {
    if (promo) {
      return;
    }
    if (!promoMeta) {
      if (promoCodeId) {
        initPromoData({
          promoId: promoCodeId,
        });
      } else if (promoCodeName) {
        initPromoData({
          codeName: promoCodeName,
          brand: tenant.value,
        });
      } else {
        initPromoData(null);
      }
    } else {
      initPromoData(null);
    }
  }, []);

  // Track the last promo ID we've synced to avoid re-syncing on every render
  const lastSyncedPromoIdRef = useRef<string | null>(null);

  // Ensure form reflects loaded promo data
  useEffect(() => {
    if (promo) {
      const promoId = promo.id_promo_code;

      // Only reset form if this is a different promo than we last synced
      if (lastSyncedPromoIdRef.current !== promoId) {
        // Ensure advanced restrictions auto-open when a promo is loaded that has advanced restrictions.
        // We only do this when switching to a different promo to avoid fighting the user's manual toggle.
        setShowAdvanced(hasAdvanceRestrictions(promo));

        reset({
          ...promo,
          code_name: promo.code_name || '',
          department_tag: promo?.department_tag || '',
          dept_tag: promo?.dept_tag || '',
          category_tag: promo?.category_tag || '',
          sub_category_tag: promo?.sub_category_tag || '',
          expires_at: promo?.expires_at ?? addDays(365).toISOString(),
          usage_count: promo?.usage_count || 0,
          first_time_purchase_only: promo?.first_time_purchase_only || false,
          first_time_app_purchase_only: promo?.first_time_app_purchase_only || false,
          email_domains: promo?.email_domains?.join(',') ?? '',
          email_addresses: promo?.email_addresses?.join(',') ?? '',
          allowed_payment_brands: promo?.allowed_payment_brands ?? [],
          allowed_payment_methods: promo?.allowed_payment_methods ?? [],
          offer_whitelist: promo?.offer_whitelist?.join(',') ?? '',
          offer_blacklist: promo?.offer_blacklist?.join(',') ?? '',
          bin_numbers: promo?.bin_numbers?.join(',') ?? '',
          promo_toggles: promo?.promo_toggles || [],
          allowed_airline_carriers: promo?.allowed_airline_carriers ?? [],
          loyalty_bonus_apply_method: normalizeLoyaltyBonusApplyMethod(promo?.loyalty_bonus_apply_method),
        });
        setPromoDiscountData(promo.promo_discounts);
        setPendingCopiedPromoConditions([]);
        lastSyncedPromoIdRef.current = promoId;
      }
    } else {
      // Reset ref when promo is cleared
      lastSyncedPromoIdRef.current = null;
    }
    // Note: Tag selections are handled by the effect in usePromo.tsx
    // which refreshes when promo and promoMeta are available
  }, [promo, reset]);

  // Update promoDiscountData when promo changes (for rerender functionality)
  useEffect(() => {
    if (promo?.promo_discounts) {
      setPromoDiscountData(promo.promo_discounts);
    }
  }, [promo?.promo_discounts]);

  const handleDiscountRowSelect = (discountId: string) => {
    const relatedConditions = promo?.promo_conditions?.filter(
      (condition) =>
        condition.promo_code_discount_ids?.includes(discountId) ||
        !condition.promo_code_discount_ids ||
        condition.promo_code_discount_ids.length === 0,
    );
    if (!relatedConditions) {
      return;
    }
    setSelectedConditionIds(relatedConditions.map((condition) => condition.id_promo_code_condition) ?? []);
    setSelectedDiscountIds([]);
  };

  const handleConditionRowSelect = (condition: PromoCondition | null) => {
    setSelectedDiscountIds(condition?.promo_code_discount_ids?.length > 0 ? condition?.promo_code_discount_ids : 'all');
    setSelectedConditionIds([]);
  };

  const handleDiscountCopySaved = useCallback(
    async ({ sourceDiscountId, copiedDiscountId }: { sourceDiscountId: string; copiedDiscountId: string }) => {
      const updateConditionFilter = <T extends { promo_code_discount_ids?: Array<string> }>(condition: T) =>
        addCopiedDiscountIdToConditionFilter(condition, sourceDiscountId, copiedDiscountId);

      setPendingCopiedPromoConditions((prevConditions) =>
        prevConditions.map((condition) => updateConditionFilter(condition) as PendingCopiedPromoCondition),
      );

      const conditionsToUpdate =
        promo?.promo_conditions
          ?.filter(
            (condition) =>
              condition.promo_code_discount_ids?.includes(sourceDiscountId) &&
              !condition.promo_code_discount_ids.includes(copiedDiscountId),
          )
          .map(updateConditionFilter) ?? [];

      await Promise.all(
        conditionsToUpdate.map((condition) =>
          createOrUpdatePromoCondition({
            promoCodeCondition: {
              id_promo_code_condition: condition.id_promo_code_condition,
              condition_type: condition.condition_type,
              condition_value:
                typeof condition.condition_value === 'string'
                  ? condition.condition_value
                  : String(condition.condition_value ?? ''),
              condition_apply_type: condition.condition_apply_type,
              id_promo_code: condition.id_promo_code,
              customized_message:
                condition.customized_message === '' ? undefined : (condition.customized_message ?? undefined),
              promo_code_discount_ids: condition.promo_code_discount_ids ?? [],
            },
          }),
        ),
      );
    },
    [promo?.promo_conditions],
  );

  const codeNameRef = useRef<string | null>(null);

  const handleReset = () => {
    setSelectedDiscountIds([]);
    setSelectedConditionIds([]);
  };

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'code_name' && typeof value.code_name === 'string') {
        const newCodeName = value.code_name;
        if (newCodeName.length > 3 && codeNameRef.current !== newCodeName) {
          codeNameRef.current = newCodeName;
          checkPromo({
            codeName: newCodeName,
            brand: 'luxuryescapes',
          });
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, checkPromo]);

  if (isLoading) {
    return <Spinner />;
  }

  const renderUnsavedChangesAlert = (message: string) => {
    if (!hasUnsavedChanges) {
      return null;
    }

    return (
      <Alert
        severity="info"
        sx={{
          alignItems: 'flex-start',
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <AlertTitle>Unsaved changes</AlertTitle>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {message}
          </Typography>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {promo ? 'Save changes' : 'Create promo'}
          </Button>
        </Stack>
      </Alert>
    );
  };

  const handleFormSubmit = handleSubmit(onSubmit);

  return (
    <form onSubmit={handleFormSubmit}>
      {promo && promo.promo_warnings?.length > 0 && (
        <Grid container spacing={2}>
          {promo.promo_warnings.map((warning) => (
            <Grid size={{ xs: 12 }} key={`${warning.code}-${warning.id_promo_code}-${warning.id_promo_code_discount}`}>
              <Alert title={JSON.stringify(warning, null, 4)} severity={warning.level as AlertColor}>
                <AlertTitle>{warning.code}</AlertTitle>
                {warning.message || 'Please review this warning before proceeding.'}
                <PromoWarningAction promoWarning={warning} promoCodeName={promo?.code_name} />
              </Alert>
            </Grid>
          ))}
        </Grid>
      )}
      <Grid container spacing={3}>
        {isPromoMetaLoading ? (
          <Spinner />
        ) : (
          <Grid size={{ xs: 12 }}>
            <PromoSummaryActions
              promoCode={promo}
              promoMeta={promoMeta}
              parentPromoCode={promoParent}
              createSinglePromoClone={createSinglePromoClone}
            />
          </Grid>
        )}
        <PromoFormSection
          title="Promo details"
          description="Choose a code and promo type, then describe its purpose and assign reporting categories. Fields marked * are required."
        >
          <Controller
            key="code_name"
            name="code_name"
            control={control}
            render={({ field: { ref, ...field }, fieldState }) => (
              <Grid size={{ xs: 12, lg: 6 }}>
                <TextField
                  {...field}
                  inputRef={ref}
                  disabled={!!promo}
                  fullWidth
                  label="Code Name"
                  required
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message ||
                    'Use letters, numbers, underscores and dashes. Cannot be changed after creation.'
                  }
                  autoFocus
                />
              </Grid>
            )}
            rules={{
              required: 'Promo code name is required',
              pattern: {
                value: /^[A-Za-z0-9_-]+$/,
                message: 'Promo code name can only include alphanumeric characters, underscores, and dashes',
              },
            }}
          />
          <Controller
            key="promo_type"
            name="promo_type"
            control={control}
            render={({ field: { ref, ...field }, fieldState }) => (
              <Grid size={{ xs: 12, lg: 6 }}>
                <Autocomplete
                  {...field}
                  disabled={!!promo}
                  options={promoTypeOptions.map((type) => type.key)}
                  getOptionLabel={(option) =>
                    promoTypeOptions.find((type) => type.key === option)?.description ?? option
                  }
                  onChange={(e, newValue) => {
                    field.onChange(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={ref}
                      required
                      variant="outlined"
                      label="Promo Type"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
            )}
            rules={{ required: 'Choose a promo type' }}
          />

          <Controller
            key="description"
            name="description"
            control={control}
            render={({ field: { ref, ...field }, fieldState }) => (
              <Grid size={{ xs: 12 }}>
                <TextField
                  {...field}
                  inputRef={ref}
                  fullWidth
                  label="Description"
                  required
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || 'Describe the purpose of this promotion.'}
                />
              </Grid>
            )}
            rules={{ required: 'Enter a description for this promo' }}
          />
          {isPromoMetaLoading && <Spinner />}
          <Grid size={{ xs: 12, md: 6 }}>
            <DepartmentTagSelector
              control={control}
              promoTagArrays={promoTagArrays}
              promoTagSelections={promoTagSelections}
              setPromoTagSelection={setPromoTagSelection}
              isPromoMetaLoading={isPromoMetaLoading}
              formType="react-hook-form"
            />
          </Grid>
          {promoLoyaltyMetaConfig?.loyaltyBonusFilterMethods?.length > 0 && (
            <LoyaltyBonusApplyMethodSelector
              promoLoyaltyMetaConfig={promoLoyaltyMetaConfig}
              isPromoMetaLoading={isPromoMetaLoading}
              control={control}
              size={{ xs: 12, md: 6 }}
            />
          )}
          <Grid size={{ xs: 12, md: 6 }}>
            <CategoryTagSelector
              control={control}
              promoTagArrays={promoTagArrays}
              promoTagSelections={promoTagSelections}
              setPromoTagSelection={setPromoTagSelection}
              isPromoMetaLoading={isPromoMetaLoading}
              formType="react-hook-form"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <SubCategoryTagSelector
              control={control}
              promoTagArrays={promoTagArrays}
              promoTagSelections={promoTagSelections}
              setPromoTagSelection={setPromoTagSelection}
              isPromoMetaLoading={isPromoMetaLoading}
              formType="react-hook-form"
            />
          </Grid>
        </PromoFormSection>
        <PromoFormSection
          title="Discounts & rewards"
          description="Add the value, eligible products and minimum spend for each discount or reward."
        >
          <Grid size={{ xs: 12 }}>
            {!isPromoMetaLoading && promoMeta ? (
              <PromoDiscountFormV4
                promoCode={promo}
                promoWarnings={promo?.promo_warnings}
                parentPromo={promoParent}
                promoType={promoType}
                promoDiscountData={promoDiscountData}
                setPromoDiscountData={setPromoDiscountData}
                promoProductTypes={promoMeta.promo_product_types}
                promoRegionGroupings={promoMeta.promo_region_groupings}
                promoTypes={promoMeta.promo_types}
                promoMetaLoading={isPromoMetaLoading}
                rerender={handleRerender}
                highlightedDiscountIds={selectedDiscountIds}
                onDiscountRowSelect={handleDiscountRowSelect}
                onDiscountCopySaved={handleDiscountCopySaved}
                promoLoyaltyMetaConfig={promoLoyaltyMetaConfig}
                promoLoyaltyBonusApplyMethod={watch('loyalty_bonus_apply_method')}
                showCopyAction
                hideHeading
              />
            ) : (
              <Spinner />
            )}
            {renderUnsavedChangesAlert(
              'You have unsaved promo changes. Submit to save your discount, reward, and form updates.',
            )}
          </Grid>
        </PromoFormSection>
        <PromoFormSection
          title="Usage & restrictions"
          description={
            promo
              ? 'Manage the expiry, usage limits and conditions for this promo.'
              : 'Set the expiry and usage limits. You can add detailed conditions after creating the promo.'
          }
        >
          {(selectedDiscountIds.length > 0 || selectedConditionIds.length > 0) && (
            <Grid size={{ xs: 12 }}>
              <Button onClick={handleReset}>Reset Highlighting</Button>
            </Grid>
          )}

          {promoMeta && promo?.id_promo_code && (
            <Grid size={{ xs: 12 }}>
              <PromoConditions
                promoCode={promo}
                promoCodeConditionTypeConfig={promoCodeConditionTypeConfig}
                onConditionRowSelect={handleConditionRowSelect}
                highlightedConditionIds={selectedConditionIds}
                onConditionUpdate={handleRerender}
                pendingCopiedConditions={pendingCopiedPromoConditions}
                onPendingCopiedConditionsChange={setPendingCopiedPromoConditions}
              />
              {renderUnsavedChangesAlert(
                'You have unsaved promo changes. Submit to save your condition updates before leaving this page.',
              )}
            </Grid>
          )}
          <Controller
            key="expires_at"
            name="expires_at"
            control={control}
            render={({ field }) => (
              <Grid size={{ xs: 12 }}>
                <DateTimeWidget format="DD/MM/YYYY hh:mm a" label="Expiry Date" {...field} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  A 24-hour grace period applies after expiry. To stop use immediately, enable “Disallow all promo
                  requests” in Advanced Restrictions.
                </Typography>
              </Grid>
            )}
          />
          <Controller
            key="code_limit"
            name="code_limit"
            control={control}
            rules={{ required: true, min: 1 }}
            render={({ field }) => (
              <Grid size={{ xs: 12, lg: 4 }}>
                <TextField
                  required
                  {...field}
                  fullWidth
                  label="Max Uses"
                  type="number"
                  onChange={(e) => {
                    const targetValue = parseInt(e.target.value);
                    field.onChange(Math.max(targetValue, 1));
                  }}
                />
                {promoParent && promo && (
                  <ParentPromoUsageLimitWarning
                    parentPromo={promoParent}
                    childPromo={promo}
                    onEmancipated={handleRerender}
                  />
                )}
              </Grid>
            )}
          />
          <Controller
            key="limit_per_user"
            name="limit_per_user"
            control={control}
            rules={{ required: true, min: 1 }}
            render={({ field }) => (
              <Grid size={{ xs: 12, lg: 4 }}>
                <TextField
                  required
                  {...field}
                  fullWidth
                  label="Max Uses per Customer"
                  type="number"
                  onChange={(e) => {
                    const targetValue = parseInt(e.target.value);
                    field.onChange(Math.max(targetValue, 1));
                  }}
                />
              </Grid>
            )}
          />
          <Controller
            key="limit_per_user_per_year"
            name="limit_per_user_per_year"
            control={control}
            rules={{ required: false, min: 0 }}
            render={({ field }) => (
              <Grid size={{ xs: 12, lg: 4 }}>
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  fullWidth
                  label="Max Uses per Customer per year"
                  type="number"
                  onChange={(e) => {
                    const targetValue = parseInt(e.target.value, 10);
                    field.onChange(Number.isNaN(targetValue) ? undefined : Math.max(targetValue, 0));
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Past 365 days. 0 = unlimited.
                </Typography>
              </Grid>
            )}
          />
        </PromoFormSection>
        <PromoFormSection
          title="Customer messaging"
          description="Optional text shown to customers when the promo is applied."
        >
          <Grid size={{ xs: 12 }}>
            <Box my={2}>
              <Controller
                key="popup_message"
                name="popup_message"
                control={control}
                render={({ field }) => (
                  <Grid size={{ xs: 12 }}>
                    <TextField {...field} fullWidth multiline label="Popup Message" />
                  </Grid>
                )}
              />
            </Box>
            {promo?.popup_message_presented && (
              <Box my={2}>
                <TextField value={promo?.popup_message_presented} disabled fullWidth multiline label="Popup Message" />
              </Box>
            )}
            {promoMeta && <PromoReplacementExplainer promoTextReplacements={promoMeta.promo_text_replacements} />}
          </Grid>
        </PromoFormSection>
        <Grid size={{ xs: 12 }}>
          <Button variant="text" aria-expanded={showAdvanced} onClick={() => setShowAdvanced(!showAdvanced)}>
            {showAdvanced ? 'Hide ' : 'Show '} Advanced Restrictions
          </Button>
        </Grid>
        {showAdvanced && (
          <PromoFormSection
            title="Advanced restrictions"
            description="Optional controls for customer eligibility, devices and payment methods."
          >
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={2}>
                <Controller
                  key="first_time_purchase_only"
                  name="first_time_purchase_only"
                  control={control}
                  render={({ field }) => (
                    <Grid size={{ xs: 12 }}>
                      <FormControlLabel
                        control={<Checkbox {...field} defaultChecked={promo?.first_time_purchase_only} />}
                        label="First time purchase only"
                        title="If checked, only customers who have not placed an order can use the promo (brand specific)"
                      />
                    </Grid>
                  )}
                />
                <Controller
                  key="first_time_app_purchase_only"
                  name="first_time_app_purchase_only"
                  control={control}
                  render={({ field }) => (
                    <Grid size={{ xs: 12 }}>
                      <FormControlLabel
                        control={<Checkbox {...field} defaultChecked={promo?.first_time_app_purchase_only} />}
                        label="First time app purchase only"
                        title="If checked, only customers who have not placed an order in a mobile app can use the promo (brand specific)"
                      />
                    </Grid>
                  )}
                />
              </Grid>
              {!isPromoMetaLoading && promoMeta.promo_toggles.length > 0 && (
                <Controller
                  key="promo_toggles"
                  name="promo_toggles"
                  control={control}
                  render={({ field }) => (
                    <Grid size={{ xs: 12 }}>
                      <SummaryToggleAccordion
                        title="Promo Toggles"
                        options={promoMeta.promo_toggles}
                        selectedKeys={field.value ?? []}
                        onChange={field.onChange}
                      />
                    </Grid>
                  )}
                />
              )}
            </Grid>
            <Controller
              key="device_types"
              name="device_types"
              control={control}
              render={({ field }) => (
                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    {...field}
                    multiple
                    options={PROMO_DEVICE_TYPES.slice(1).map((type) => type.key)}
                    getOptionLabel={(option) => PROMO_DEVICE_TYPES.find((type) => type.key === option)?.value}
                    renderInput={(params) => (
                      <TextField fullWidth {...params} variant="outlined" label="Restrict to Device Types"></TextField>
                    )}
                    onChange={(e: unknown, newValue: Array<string> | null) => {
                      field.onChange(newValue);
                    }}
                    // Styling the dropdown component
                    componentsProps={{
                      paper: {
                        sx: {
                          boxShadow: `0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)`,
                        },
                      },
                    }}
                  />
                </Grid>
              )}
            />

            <Grid size={{ xs: 12 }}>
              <Controller
                key="email_addresses"
                name="email_addresses"
                control={control}
                render={({ field }) => (
                  <Tooltip title="Multiple email address(es) must be comma separated.">
                    <TextField {...field} fullWidth label="Restrict to Email Address(es)" />
                  </Tooltip>
                )}
              />
            </Grid>
            <Controller
              key="email_domains"
              name="email_domains"
              control={control}
              render={({ field }) => (
                <Grid size={{ xs: 12 }}>
                  <Tooltip title="(Should NOT include the @ at the beginning - e.g. example.com not @example.com). Multiple email domains must be comma separated">
                    <TextField {...field} fullWidth label="Restrict to Email Domain(s)" />
                  </Tooltip>
                  {isCorporateBenefitOn && watch('email_domains').length > 0 && (
                    <Alert severity="error">Email domains are required for corporate promo codes</Alert>
                  )}
                </Grid>
              )}
            />
            <Grid size={{ xs: 12 }}>
              <Controller
                key="offer_whitelist"
                name="offer_whitelist"
                control={control}
                render={({ field }) => (
                  <Tooltip title="Multiple deal ID(s) must be comma separated.">
                    <TextField
                      {...field}
                      fullWidth
                      disabled={offerBlackList && offerBlackList.length > 0}
                      label="Restrict to Deal ID(s) [deprecated - see offer-id condition]"
                    />
                  </Tooltip>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                key="offer_blacklist"
                name="offer_blacklist"
                control={control}
                render={({ field }) => (
                  <Tooltip title="Multiple deal ID(s) must be comma separated.">
                    <TextField
                      {...field}
                      fullWidth
                      disabled={offerWhiteList && offerWhiteList.length > 0}
                      label="Exclude Deal ID(s)  [deprecated - see offer-id condition]"
                    />
                  </Tooltip>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PageSubheader title="Payment Restrictions">
                These restrictions are applied when the payment is made
              </PageSubheader>
            </Grid>
            <AllowedPaymentBrandSelect
              selectedValues={watch('allowed_payment_brands') ?? []}
              control={control}
              allowedPaymentBrands={promoMeta.promo_allowed_payment_brands ?? []}
              isPromoMetaLoading={isPromoMetaLoading}
            />
            <AllowedPaymentMethodSelect
              selectedValues={watch('allowed_payment_methods')}
              control={control}
              promoMeta={promoMeta}
              isPromoMetaLoading={isPromoMetaLoading}
            />
            <Grid size={{ xs: 12 }}>
              <Controller
                key="bin_numbers"
                name="bin_numbers"
                control={control}
                render={({ field }) => (
                  <Tooltip title="You must restrict to at least Stripe and/or Stripe 3DS payment methods. Multiple BIN numbers must be comma separated.">
                    <TextField {...field} fullWidth label="Restrict to Bank Identification Number(s)" />
                  </Tooltip>
                )}
              />
            </Grid>
          </PromoFormSection>
        )}
        {showAdvanced && (
          <PromoFormSection
            title="Corporate benefits"
            description="Restrict a corporate promo to an eligible company and its email domains."
          >
            <Controller
              key="is_corporate"
              name="is_corporate"
              control={control}
              render={({ field }) => (
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={<Checkbox defaultChecked={isCorporateBenefitOn} {...field} />}
                    label="Corporate promo code"
                  />
                </Grid>
              )}
            />
            {isCorporateBenefitOn && (
              <Controller
                key="corporate_name"
                name="corporate_name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Grid size={{ xs: 12 }}>
                    <TextField disabled={!!promo} fullWidth required {...field} label="Company Name"></TextField>
                  </Grid>
                )}
              />
            )}
            {isCorporateBenefitOn && (
              <Controller
                key="email_domains"
                name="email_domains"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!isCorporateBenefitOn) {
                      return true;
                    }
                    return value && value.trim().length > 0
                      ? true
                      : 'Email domains are required for corporate promo codes';
                  },
                }}
                render={({ field, fieldState }) => (
                  <Grid size={{ xs: 12 }}>
                    <Tooltip title="(Should NOT include the @ at the beginning - e.g. example.com not @example.com). Multiple email domains must be comma separated">
                      <TextField
                        {...field}
                        fullWidth
                        required
                        label="Eligible Email Domains"
                        error={!!fieldState.error}
                        helperText={fieldState.error ? fieldState.error.message : ''}
                      />
                    </Tooltip>
                  </Grid>
                )}
              />
            )}
          </PromoFormSection>
        )}
        {showAdvanced && !!promo && (
          <Grid size={{ xs: 12 }}>
            <PageSubheader title="Users">
              <Box component="span" color="text.secondary" alignItems="left">
                Users that qualify for this promo
              </Box>
            </PageSubheader>
            {!promo.cloned_from_promo_id &&
              !promo.fk_referral_code_id &&
              hasIsParentPromoToggle(promo.promo_toggles) && <PromoChildSearchResults codeName={promo.code_name} />}
            <PromoUsers promoId={promo?.id_promo_code} promoCode={promo} />
            <PromoUserBatchUpload codeName={promo?.code_name} promoExpiresAt={promo?.expires_at} />
          </Grid>
        )}
      </Grid>
      <StickyFormActions>
        <Button type="submit" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : promo ? 'Save changes' : 'Create promo'}
        </Button>
        <Typography variant="body2" color="text.secondary">
          {promo ? 'Saves all promo sections.' : 'Review the discounts, expiry and restrictions before creating.'}
        </Typography>
        {promo && (
          <PromoApproveButton
            codeName={promo.code_name}
            brand={promo.brand}
            isApprovedBy={promo.approved_by}
            isApprovedAt={promo.approved_at}
          />
        )}
        {errors.length > 0 && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {errors}
          </Alert>
        )}
      </StickyFormActions>
    </form>
  );
}

export default PromoFormV4;
